import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import moment from 'moment';
import { BILL_ALLOCATION_STATE } from 'src/api/billAutomation/bills';
import { BILL_STATUSES } from './companyBillRows';
import { RECEIVED_WITHIN_OPTIONS } from './billsFilters';

/**
 * What the Bills list asks the server for, and how the page's own values become it (LB-5842).
 *
 * The list used to load the company's whole book and filter it in the browser, so a filter's value
 * could be whatever the table happened to render — a display label, an address joined from the
 * company's property list, a utility name three derivations from what the bill actually said. None
 * of that means anything to a database, so every one of them is translated here, in one place, and
 * nowhere else.
 *
 * Pure on purpose: no React, no store. It is the part of this change that can be tested outright.
 */

/** The API values behind the labels the status filter shows. */
export const STATUS_FILTER_OPTIONS = [{
  value: 'Pending',
  label: BILL_STATUSES.pending
}, {
  value: 'Processing',
  label: BILL_STATUSES.processing
}, {
  value: 'Extracted',
  label: BILL_STATUSES.extracted
}, {
  value: 'Validated',
  label: BILL_STATUSES.validated
}, {
  value: 'NeedsProperty',
  label: BILL_STATUSES.needsProperty
}, {
  value: 'Rejected',
  label: BILL_STATUSES.rejected
}, {
  value: 'Duplicate',
  label: BILL_STATUSES.duplicate
}, {
  value: 'Failed',
  label: BILL_STATUSES.failed
}];

/**
 * The Allocation column's states the list can be narrowed to (LB-6054), in the column's own words. A bill is listed
 * when any of its services is in one of them. "Needs review" is not offered: the column shows a table held for a
 * bill-spike review as awaiting approval, and the service files it there too.
 */
export const ALLOCATION_FILTER_OPTIONS = [{
  value: BILL_ALLOCATION_STATE.notAllocated,
  label: 'Not allocated'
}, {
  value: BILL_ALLOCATION_STATE.allocating,
  label: 'Allocating'
}, {
  value: BILL_ALLOCATION_STATE.awaitingApproval,
  label: 'Awaiting approval'
}, {
  value: BILL_ALLOCATION_STATE.failed,
  label: 'Failed'
}, {
  value: BILL_ALLOCATION_STATE.skipped,
  label: 'Skipped'
}, {
  value: BILL_ALLOCATION_STATE.allocated,
  label: 'Allocated'
}];

/**
 * Validated bills that are, or are not, enrolled in AP Push (LB-6031): the AP work list, and what nobody means to push.
 * One or the other, since every Validated bill is one of the two; a bill in any other status is in neither.
 */
export const AP_PUSH_FILTER_OPTIONS = [{
  value: 'Enrolled',
  label: 'Enrolled'
}, {
  value: 'NotEnrolled',
  label: 'Not enrolled'
}];

/** The enrolment a link names, or none when it names something the filter does not offer. */
export function apPushEnrolmentFromUrl(value) {
  return AP_PUSH_FILTER_OPTIONS.some(option => option.value === value) ? value : '';
}

/**
 * The tabs across the top (LB-6087): work lists, each one a preset over the Status and Allocation filters.
 *
 * The tab used to be state of its own, ANDed with the Status filter out of sight: "Needs review" with "Error" picked
 * showed nothing, and nothing on the page said why. Now a tab sets those two filters and leaves the rest alone, and
 * which tab is lit is read back from them, so the chips always say what the list is filtered by. Changing Status or
 * Allocation by hand simply lights no tab.
 *
 * The two allocation tabs carry no count: one would need the company's whole allocation book read on every load.
 */
export const BILL_TABS = [{
  key: 'all',
  label: 'All bills',
  statuses: [],
  allocationStates: []
}, {
  key: 'needsReview',
  label: 'Needs review',
  statuses: ['NeedsProperty'],
  allocationStates: []
}, {
  key: 'awaitingApproval',
  label: 'Awaiting approval',
  statuses: [],
  allocationStates: [BILL_ALLOCATION_STATE.awaitingApproval]
}, {
  key: 'notAllocated',
  label: 'Not allocated',
  statuses: [],
  allocationStates: [BILL_ALLOCATION_STATE.notAllocated]
}, {
  key: 'processed',
  label: 'Processed',
  statuses: ['Validated'],
  allocationStates: []
}];

/** A tab that narrows by allocation. Not offered over one email's bills, whose tabs count the rows in hand. */
export const isAllocationTab = tab => tab.allocationStates.length > 0;
const sameMembers = (a, b) => a.length === b.length && a.every(value => b.includes(value));

/** The tab the filters amount to, or null when they amount to none of them. */
export function activeTabKey(filters) {
  var _BILL_TABS$find$key, _BILL_TABS$find;
  return (_BILL_TABS$find$key = (_BILL_TABS$find = BILL_TABS.find(tab => sameMembers(tab.statuses, filters.statuses) && sameMembers(tab.allocationStates, filters.allocationStates))) === null || _BILL_TABS$find === void 0 ? void 0 : _BILL_TABS$find.key) !== null && _BILL_TABS$find$key !== void 0 ? _BILL_TABS$find$key : null;
}

/** The two filters a tab sets. Everything else stays as it was. */
export function tabFilters(key) {
  const tab = BILL_TABS.find(candidate => candidate.key === key);
  return tab ? {
    statuses: [...tab.statuses],
    allocationStates: [...tab.allocationStates]
  } : null;
}

/**
 * Whether a row is in the statuses asked for. Only for one email's bills (LB-5828), which are read whole and narrowed
 * here, so the tabs over them can keep counting the whole email: "this email brought 10 bills, 3 need review".
 */
export function rowMatchesStatuses(row, statuses) {
  return statuses.length === 0 || statuses.includes(row.extractStatus);
}

/**
 * The service types behind each utility the filter offers.
 *
 * The extractor is held to a closed list — electric, electric_generation, gas, water, sewer,
 * stormwater, trash, landscaping, irrigation, internet, cable, satellite, other — and is told to
 * pick from it by what a section charges for rather than by the words it uses, so "Refuse
 * Collection" arrives as `trash`. That is why this is a fixed map and not a guess: the normalising
 * already happened, upstream, at extraction.
 *
 * `Other` carries `stormwater` with it deliberately. The page reads a stormwater service as Other
 * today — a word that merely contains a utility word names nothing, so that stormwater is not billed
 * as water — and moving the filter to the server is not the moment to change what it matches.
 */
export const SERVICE_TYPES_BY_UTILITY = {
  'Cable/Satellite': ['cable', 'satellite'],
  Electricity: ['electric', 'electric_generation'],
  Gas: ['gas'],
  Internet: ['internet'],
  Landscaping: ['landscaping', 'irrigation'],
  Other: ['other', 'stormwater'],
  Sewer: ['sewer'],
  Trash: ['trash'],
  Water: ['water']
};
export const UTILITY_FILTER_OPTIONS = Object.keys(SERVICE_TYPES_BY_UTILITY).sort((a, b) => a.localeCompare(b));

/**
 * The columns the server can order by, keyed as the table knows them.
 *
 * Absent on purpose: the AppFolio push state blends what this browser did a moment ago and the
 * server cannot see it; the allocation figure is a live call to another service; and the amount
 * comes back out of the payload as text, where 9.99 sorts after 100.00. A header that orders the
 * rows you can see and calls that sorting the list is worse than a header that does not offer.
 */
export const SORTABLE_COLUMNS = {
  account: 'accountNumber',
  dueDate: 'dueDate',
  fileName: 'fileName',
  from: 'sender',
  invoiceNumber: 'invoiceNumber',
  provider: 'provider',
  received: 'acquired',
  servicePeriod: 'servicePeriod',
  status: 'status'
};

/**
 * How many bills of one acquisition event are read at once.
 *
 * The service caps a page here, and a batch is bounded by what a single email or upload carried, so
 * in practice the event arrives whole. That is what lets the deep link keep counting its tabs and
 * its banner from the rows it holds.
 */
export const BATCH_PAGE_SIZE = 100;
export const EMPTY_BILLS_QUERY = {
  batchId: null,
  filters: {
    allocationStates: [],
    apPushEnrolment: '',
    properties: [],
    providers: [],
    receivedWithin: '',
    // The service period a bill has to overlap, as `YYYY-MM-DD`; either end may be open (LB-6087).
    servicePeriodFrom: '',
    servicePeriodTo: '',
    statuses: [],
    utilities: []
  },
  order: 'desc',
  orderBy: 'received',
  page: 0,
  // As many as the service sends in one page (LB-6034), so a full upload fits on one.
  pageSize: 100,
  search: ''
};

/**
 * Where each filter lives in the address (LB-6087), so a reload or a shared link lands on the same list. `allocation`
 * (LB-6054) and `apPush` (LB-6031) are the names links already carry. Lists of fixed values are comma-joined, in the
 * order the filter offers them; service providers are free text that can hold a comma, so each is a key of its own.
 */
export const FILTER_URL_PARAMS = {
  statuses: 'status',
  allocationStates: 'allocation',
  apPushEnrolment: 'apPush',
  properties: 'property',
  utilities: 'utility',
  providers: 'provider',
  receivedWithin: 'received',
  servicePeriodFrom: 'periodFrom',
  servicePeriodTo: 'periodTo'
};

/**
 * What a link may carry. A pasted link is input like any other, and every value in it goes to the service: a property
 * is bound there as a Guid and a month as a date, so anything else would fail the whole list with a 400. The caps keep
 * the address -- and the request it becomes -- under the service's 8 KB request line.
 *
 * Applied the same way when the address is written as when it is read, so a pick round-trips unchanged and the two
 * sides never disagree about what is on. A pick past a cap still filters the list; only the address leaves it out.
 */
const MAX_LINKED_PROVIDERS = 30;
const MAX_PROVIDER_LENGTH = 100;
const MAX_LINKED_PROPERTIES = 50;
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const LINKABLE_DATE = /^20\d\d-\d\d-\d\d$/;
const commaListFromUrl = (params, name) => params.getAll(name).flatMap(value => value.split(',')).map(value => value.trim()).filter(Boolean);

/** Kept in the order the options are offered, so one choice reads the same in the address however it was made. */
const inOfferedOrder = (values, offered) => offered.filter(value => values.includes(value));
const sortedUnique = values => [...new Set(values)].sort((a, b) => a.localeCompare(b));
const STATUS_VALUES = STATUS_FILTER_OPTIONS.map(option => option.value);
const ALLOCATION_VALUES = ALLOCATION_FILTER_OPTIONS.map(option => option.value);
const RECEIVED_VALUES = RECEIVED_WITHIN_OPTIONS.map(option => option.value);
const linkableProperties = ids => sortedUnique(ids.filter(id => GUID.test(id))).slice(0, MAX_LINKED_PROPERTIES);
// Not trimmed: the service matches a provider's name exactly, spaces and all.
const linkableProviders = names => sortedUnique(names.filter(name => name && name.length <= MAX_PROVIDER_LENGTH)).slice(0, MAX_LINKED_PROVIDERS);
const linkableDate = date => LINKABLE_DATE.test(date !== null && date !== void 0 ? date : '') && moment(date, moment.HTML5_FMT.DATE, true).isValid() ? date : '';

/** A service period read from a link, its ends put in order: a range typed backwards still means the same days. */
function linkablePeriod(from, to) {
  const _ref = [linkableDate(from), linkableDate(to)],
    start = _ref[0],
    end = _ref[1];
  return start && end && start > end ? [end, start] : [start, end];
}

/** Every filter a link names, keeping only values each filter could have offered. */
export function filtersFromUrl(params) {
  var _params$get;
  const received = (_params$get = params.get(FILTER_URL_PARAMS.receivedWithin)) !== null && _params$get !== void 0 ? _params$get : '';
  const _linkablePeriod = linkablePeriod(params.get(FILTER_URL_PARAMS.servicePeriodFrom), params.get(FILTER_URL_PARAMS.servicePeriodTo)),
    _linkablePeriod2 = _slicedToArray(_linkablePeriod, 2),
    periodFrom = _linkablePeriod2[0],
    periodTo = _linkablePeriod2[1];
  return {
    allocationStates: inOfferedOrder(commaListFromUrl(params, FILTER_URL_PARAMS.allocationStates), ALLOCATION_VALUES),
    apPushEnrolment: apPushEnrolmentFromUrl(params.get(FILTER_URL_PARAMS.apPushEnrolment)),
    properties: linkableProperties(commaListFromUrl(params, FILTER_URL_PARAMS.properties)),
    providers: linkableProviders(params.getAll(FILTER_URL_PARAMS.providers)),
    receivedWithin: RECEIVED_VALUES.includes(received) ? received : '',
    servicePeriodFrom: periodFrom,
    servicePeriodTo: periodTo,
    statuses: inOfferedOrder(commaListFromUrl(params, FILTER_URL_PARAMS.statuses), STATUS_VALUES),
    utilities: inOfferedOrder(commaListFromUrl(params, FILTER_URL_PARAMS.utilities), UTILITY_FILTER_OPTIONS)
  };
}

/** The filters as address entries, in one canonical form: the same filters always make the same address. */
function filtersToUrlEntries(filters) {
  const _linkablePeriod3 = linkablePeriod(filters.servicePeriodFrom, filters.servicePeriodTo),
    _linkablePeriod4 = _slicedToArray(_linkablePeriod3, 2),
    periodFrom = _linkablePeriod4[0],
    periodTo = _linkablePeriod4[1];
  const joined = (key, values) => values.length ? [[FILTER_URL_PARAMS[key], values.join(',')]] : [];
  const single = (key, value) => value ? [[FILTER_URL_PARAMS[key], value]] : [];
  return [...joined('statuses', inOfferedOrder(filters.statuses, STATUS_VALUES)), ...joined('allocationStates', inOfferedOrder(filters.allocationStates, ALLOCATION_VALUES)), ...single('apPushEnrolment', apPushEnrolmentFromUrl(filters.apPushEnrolment)), ...joined('properties', linkableProperties(filters.properties)), ...joined('utilities', inOfferedOrder(filters.utilities, UTILITY_FILTER_OPTIONS)), ...linkableProviders(filters.providers).map(name => [FILTER_URL_PARAMS.providers, name]), ...single('receivedWithin', RECEIVED_VALUES.includes(filters.receivedWithin) ? filters.receivedWithin : ''), ...single('servicePeriodFrom', periodFrom), ...single('servicePeriodTo', periodTo)];
}

/** What the filters look like in the address. Two sets of filters are the same list when these match. */
export function filtersUrlKey(filters) {
  return new URLSearchParams(filtersToUrlEntries(filters)).toString();
}

/** The address with these filters in place of whatever filters it had, and everything else in it kept. */
export function withFiltersInUrl(params, filters) {
  const next = new URLSearchParams(params);
  Object.values(FILTER_URL_PARAMS).forEach(name => next.delete(name));
  filtersToUrlEntries(filters).forEach(_ref2 => {
    let _ref3 = _slicedToArray(_ref2, 2),
      name = _ref3[0],
      value = _ref3[1];
    return next.append(name, value);
  });
  return next;
}

/** The first view of the list, with every filter a link carried, so the first read is already the filtered one. */
export function billsQueryFromUrl(params) {
  return _objectSpread(_objectSpread({}, EMPTY_BILLS_QUERY), {}, {
    filters: filtersFromUrl(params)
  });
}

/**
 * A "last N days" window, sent as the count of days rather than as the date it starts on.
 *
 * The browser used to work the boundary out itself, which was wrong twice over. The service draws
 * the tiles above this table against its own clock, and a window floored to local midnight is not
 * the window floored to midnight UTC — so the filter and the figures over it could be describing
 * different days to the same person. And a date computed per render puts a moving value in the key
 * an effect re-fetches on, which is an unbounded request loop; it had to be frozen to the day to be
 * safe at all. A count of days is a constant: no clock in the key, and one clock on the page.
 */
export function receivedWithinToDays(value) {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? count : null;
}
const isSet = value => Array.isArray(value) ? value.length > 0 : value !== '' && value !== null && value !== undefined;

/** The query string for one view of the list. */
export function toBillsRequestParams(query) {
  var _SORTABLE_COLUMNS$que;
  // Under a batch scope the statuses stay in the browser, and that is not an inconsistency.
  //
  // The tabs and the banner over a batch describe the acquisition event -- "this email brought 10
  // bills, 3 of them need review" -- and they are counted from the rows in hand, because the
  // summary beside them describes the company instead. Sending the statuses would narrow those
  // rows and leave the counts describing themselves: open "Needs review" on a 10-bill email and it
  // would report the email had brought 3 bills and processed none of them. The whole event fits in
  // one page, which is what makes narrowing and counting it here sound (`rowMatchesStatuses`).
  const statuses = query.batchId ? [] : query.filters.statuses;
  const utilities = query.filters.utilities.flatMap(utility => {
    var _SERVICE_TYPES_BY_UTI;
    return (_SERVICE_TYPES_BY_UTI = SERVICE_TYPES_BY_UTILITY[utility]) !== null && _SERVICE_TYPES_BY_UTI !== void 0 ? _SERVICE_TYPES_BY_UTI : [];
  });
  const params = {
    // The table counts pages from zero and the service from one. Converted here and only here.
    page: query.page + 1,
    pageSize: query.pageSize,
    sortBy: (_SORTABLE_COLUMNS$que = SORTABLE_COLUMNS[query.orderBy]) !== null && _SORTABLE_COLUMNS$que !== void 0 ? _SORTABLE_COLUMNS$que : SORTABLE_COLUMNS.received,
    sortDescending: query.order === 'desc',
    search: query.search,
    statuses,
    providers: query.filters.providers,
    propertyIds: query.filters.properties,
    serviceTypes: utilities,
    allocationStates: query.filters.allocationStates,
    apPushEnrolment: query.filters.apPushEnrolment,
    receivedWithinDays: receivedWithinToDays(query.filters.receivedWithin),
    // The service lists a bill whose service period overlaps these, so either end can be left open.
    serviceFrom: query.filters.servicePeriodFrom || null,
    serviceTo: query.filters.servicePeriodTo || null,
    batchId: query.batchId
  };

  // Anything unset is left out rather than sent empty: `statuses=` binds as one blank value and
  // matches nothing, which looks exactly like a filter nobody can see.
  return Object.fromEntries(Object.entries(params).filter(_ref4 => {
    let _ref5 = _slicedToArray(_ref4, 2),
      value = _ref5[1];
    return isSet(value);
  }));
}

/**
 * A stable identity for a view, so an effect re-reads when it changes and not when it does not.
 *
 * Nothing in the request is drawn from a clock, so this is a pure function of the view — which is
 * what makes it safe as an effect dependency at all.
 */
export function billsQueryKey(query) {
  return JSON.stringify(toBillsRequestParams(query));
}
const withPageReset = next => _objectSpread(_objectSpread({}, next), {}, {
  page: 0
});

/**
 * Every change except turning the page returns to the first one.
 *
 * Said once here rather than at each of the seven places that used to remember to, one of which —
 * sorting — did not, so sorting from page four asked for page four of a freshly ordered list.
 */
export function billsQueryReducer(query, action) {
  var _action$batchId;
  switch (action.type) {
    case 'SET_PAGE':
      return _objectSpread(_objectSpread({}, query), {}, {
        page: action.page
      });
    case 'SET_PAGE_SIZE':
      return withPageReset(_objectSpread(_objectSpread({}, query), {}, {
        pageSize: action.pageSize
      }));
    case 'SET_SEARCH':
      return withPageReset(_objectSpread(_objectSpread({}, query), {}, {
        search: action.search
      }));
    case 'SET_SORT':
      return withPageReset(_objectSpread(_objectSpread({}, query), {}, {
        order: action.order,
        orderBy: action.orderBy
      }));
    case 'SET_FILTERS':
      return withPageReset(_objectSpread(_objectSpread({}, query), {}, {
        filters: _objectSpread(_objectSpread({}, query.filters), action.filters)
      }));
    case 'CLEAR_FILTERS':
      return withPageReset(_objectSpread(_objectSpread({}, query), {}, {
        filters: _objectSpread({}, EMPTY_BILLS_QUERY.filters)
      }));
    case 'SET_BATCH':
      // One acquisition event. A batch is what one email or one upload contained, so the whole of it
      // fits in a page and the tab counts can keep being counted from the rows in hand — which is what
      // they have to do, since the summary beside them describes the company rather than this event.
      return withPageReset(_objectSpread(_objectSpread({}, query), {}, {
        batchId: (_action$batchId = action.batchId) !== null && _action$batchId !== void 0 ? _action$batchId : null,
        pageSize: action.batchId ? BATCH_PAGE_SIZE : EMPTY_BILLS_QUERY.pageSize
      }));
    default:
      return query;
  }
}